/**
 * @file
 * Breadcrumbs disclosure behavior.
 *
 * Handles the ellipsis toggle button for collapsed breadcrumb items:
 * - Click to toggle dropdown visibility
 * - Escape key to close
 * - Click outside to close
 */

((Drupal, once) => {
  'use strict';

  Drupal.behaviors.tpBreadcrumbs = {
    attach(context: Document | HTMLElement) {
      const breadcrumbs = once('tp-breadcrumbs', '.tp-breadcrumbs', context);

      breadcrumbs.forEach((nav) => {
        const toggle = nav.querySelector<HTMLButtonElement>('.tp-breadcrumbs__toggle');
        const collapsedList = nav.querySelector<HTMLElement>('.tp-breadcrumbs__dropdown');
        const list = nav.querySelector<HTMLOListElement>('.tp-breadcrumbs__list');

        let rafId: number | null = null;
        let lastAdjustPx: number | null = null;

        const applyTruncationAdjust = (adjustment: number) => {
          const clamped = Math.max(0, Math.round(adjustment));
          if (lastAdjustPx === clamped) {
            return;
          }
          lastAdjustPx = clamped;
          nav.style.setProperty('--tp-breadcrumbs-truncate-adjust', `${clamped}px`);
        };

        const updateTruncationWidth = () => {
          if (rafId !== null) {
            cancelAnimationFrame(rafId);
          }

          rafId = window.requestAnimationFrame(() => {
            if (!list) {
              return;
            }

            const items = list.querySelectorAll<HTMLElement>('.tp-breadcrumbs__item');
            if (items.length < 2) {
              nav.style.removeProperty('--tp-breadcrumbs-truncate-adjust');
              lastAdjustPx = null;
              return;
            }

            const secondToLast = items[items.length - 2];
            const label = secondToLast.querySelector<HTMLElement>('.tp-breadcrumbs__link, .tp-breadcrumbs__current');

            if (!label) {
              nav.style.removeProperty('--tp-breadcrumbs-truncate-adjust');
              lastAdjustPx = null;
              return;
            }

            // Use scrollWidth to avoid feedback loops caused by truncation affecting layout width.
            const measuredWidth = label.scrollWidth;
            const adjustment = 200 - measuredWidth;
            applyTruncationAdjust(adjustment);
          });
        };

        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(() => {
            updateTruncationWidth();
          });

          if (list) {
            resizeObserver.observe(list);
          }
        } else {
          window.addEventListener('resize', updateTruncationWidth);
        }

        updateTruncationWidth();

        if (!toggle || !collapsedList) {
          return;
        }

        // Toggle dropdown on click
        toggle.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!isExpanded));
          collapsedList.classList.toggle('hidden', isExpanded);

          // Focus first link when opening
          if (!isExpanded) {
            const firstLink = collapsedList.querySelector<HTMLElement>('a, button');
            firstLink?.focus();
          }
        });

        // Close on Escape key
        nav.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
            toggle.setAttribute('aria-expanded', 'false');
            collapsedList.classList.add('hidden');
            toggle.focus();
          }
        });

        // Close when clicking outside
        document.addEventListener('click', (event) => {
          const target = event.target as Node;
          if (!nav.contains(target) && toggle.getAttribute('aria-expanded') === 'true') {
            toggle.setAttribute('aria-expanded', 'false');
            collapsedList.classList.add('hidden');
          }
        });

        // Close when focus leaves the breadcrumbs
        nav.addEventListener('focusout', (event) => {
          const relatedTarget = (event as FocusEvent).relatedTarget as Node | null;
          // Use setTimeout to allow focus to settle
          setTimeout(() => {
            if (!nav.contains(relatedTarget) && toggle.getAttribute('aria-expanded') === 'true') {
              toggle.setAttribute('aria-expanded', 'false');
              collapsedList.classList.add('hidden');
            }
          }, 0);
        });
      });
    },
  };
})(Drupal, once);
